import React, { useState } from 'react';
import { axiosInstance } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Search, Loader2, User, AlertTriangle, Phone } from 'lucide-react';

export default function MemberLookupDialog({ open, onClose, onSelectMember, gymId }) {
  const [phone, setPhone] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [apiUnavailable, setApiUnavailable] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualData, setManualData] = useState({
    customer_name: '', customer_phone: '', customer_email: '', gym_name: '',
  });

  const handleSearch = async () => {
    if (!phone.trim() || phone.trim().length < 10) {
      toast.error('Enter a valid 10-digit phone number');
      return;
    }
    setSearching(true);
    setResults(null);
    setApiUnavailable(false);
    setShowManualForm(false);
    try {
      const params = { phone: phone.trim() };
      if (gymId) params.gym_id = gymId;
      const res = await axiosInstance.get('/supplements/lookup-member', { params });
      setResults(res.data);
    } catch (error) {
      if (error.response?.status === 503 || error.code === 'ERR_NETWORK') {
        setApiUnavailable(true);
      } else {
        toast.error(error.response?.data?.detail || 'Member lookup failed');
      }
    } finally {
      setSearching(false);
    }
  };

  const handleSelectMember = (member) => {
    onSelectMember({
      customer_name: member.name,
      customer_phone: member.phone,
      customer_email: member.email || '',
      gym_name: member.gym_name || member.gym || '',
      gym_id: member.gym_id || '',
      membership_plan: member.plan_name || '',
      membership_start: member.start_date || '',
      membership_expiry: member.expire_date || '',
      membership_price: member.price || 0,
      membership_sold_by: member.sold_by || '',
      is_annual: member.is_annual || false,
      plan_tier: member.plan_tier || (member.is_annual ? 'annual' : 'other'),
    });
    handleClose();
  };

  const handleManualSubmit = () => {
    if (!manualData.customer_name.trim() || !manualData.customer_phone.trim()) {
      toast.error('Name and phone are required');
      return;
    }
    onSelectMember({
      customer_name: manualData.customer_name.trim(),
      customer_phone: manualData.customer_phone.trim(),
      customer_email: manualData.customer_email.trim(),
      gym_name: manualData.gym_name.trim(),
      gym_id: '',
      membership_plan: '',
      membership_start: '',
      membership_expiry: '',
      membership_price: 0,
      is_annual: false,
      plan_tier: 'other',
      manual_entry: true,
    });
    handleClose();
  };

  const handleClose = () => {
    setPhone('');
    setResults(null);
    setApiUnavailable(false);
    setShowManualForm(false);
    setManualData({ customer_name: '', customer_phone: '', customer_email: '', gym_name: '' });
    onClose();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  const formatINR = (price) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(price || 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Look Up Member
          </DialogTitle>
        </DialogHeader>

        {/* Phone Search */}
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              placeholder="Enter 10-digit phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              data-testid="member-phone-input"
            />
          </div>
          <Button onClick={handleSearch} disabled={searching} data-testid="member-search-btn">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>

        {/* Search Results */}
        {results && (() => {
          const members = results.eligible_members || results.annual_members || results.members || [];
          return (
          <div className="mt-4 space-y-3">
            {results.eligible && members.length > 0 ? (
              <>
                <p className="text-sm text-muted-foreground">
                  {members.length} eligible member{members.length > 1 ? 's' : ''} found
                </p>
                {members.map((member, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleSelectMember(member)}
                    className="border rounded-lg p-3 cursor-pointer hover:bg-slate-50 hover:border-primary transition-colors"
                    data-testid={`member-card-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-500" />
                        <span className="font-medium">{member.name}</span>
                      </div>
                      {member.plan_tier === 'annual' && (
                        <Badge className="bg-green-100 text-green-700">Annual</Badge>
                      )}
                      {member.plan_tier === 'half_yearly' && (
                        <Badge className="bg-blue-100 text-blue-700">Half-Yearly</Badge>
                      )}
                      {(!member.plan_tier || member.plan_tier === 'other') && (
                        <Badge className="bg-gray-100 text-gray-600">Other</Badge>
                      )}
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-1 text-sm text-slate-600">
                      <div className="flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {member.phone}
                      </div>
                      <div>Gym: {member.gym_name || member.gym || '—'}</div>
                      <div>Plan: {member.plan_name || '—'}</div>
                      <div>Price: {formatINR(member.price)}</div>
                      <div>Start: {formatDate(member.start_date)}</div>
                      <div>Expiry: {formatDate(member.expire_date)}</div>
                      {member.sold_by && (
                        <div className="col-span-2 text-xs text-slate-500">Sold by: {member.sold_by}</div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-amber-700 mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-medium">
                    {results.reason || 'No eligible members found for this phone number'}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowManualForm(true);
                    setManualData(prev => ({ ...prev, customer_phone: phone }));
                  }}
                  data-testid="proceed-manually-btn"
                >
                  Proceed Manually
                </Button>
              </div>
            )}
          </div>
        );})()}

        {/* WTF API Unavailable */}
        {apiUnavailable && (
          <div className="mt-4 border border-amber-200 bg-amber-50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-amber-700 mb-2">
              <AlertTriangle className="w-4 h-4" />
              <span className="font-medium">WTF API unavailable — enter details manually</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowManualForm(true);
                setManualData(prev => ({ ...prev, customer_phone: phone }));
              }}
            >
              Enter Manually
            </Button>
          </div>
        )}

        {/* Manual Entry Form */}
        {showManualForm && (
          <div className="mt-4 border rounded-lg p-4 space-y-3">
            <p className="text-sm font-medium text-slate-700">Manual Entry</p>
            <div className="space-y-2">
              <Label>Customer Name *</Label>
              <Input
                value={manualData.customer_name}
                onChange={(e) => setManualData(prev => ({ ...prev, customer_name: e.target.value }))}
                placeholder="Full name"
                data-testid="manual-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone *</Label>
              <Input
                value={manualData.customer_phone}
                onChange={(e) => setManualData(prev => ({ ...prev, customer_phone: e.target.value }))}
                placeholder="Phone number"
                data-testid="manual-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                value={manualData.customer_email}
                onChange={(e) => setManualData(prev => ({ ...prev, customer_email: e.target.value }))}
                placeholder="Email (optional)"
              />
            </div>
            <div className="space-y-2">
              <Label>Gym Name</Label>
              <Input
                value={manualData.gym_name}
                onChange={(e) => setManualData(prev => ({ ...prev, gym_name: e.target.value }))}
                placeholder="Gym name"
              />
            </div>
            <Button onClick={handleManualSubmit} data-testid="manual-submit-btn">
              Use This Member
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
